import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import ConfirmBox from '../../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import { getStepById, LAST_STEP_ID } from '../utils/onboardingSteps.config.js';
import { getClearStepBlockReason } from '../utils/stepClear.js';
import { useWizard } from '../hooks/WizardProvider.jsx';
import useOnboardingSx from '../hooks/useOnboardingSx.js';

export default function WizardFooter({ stepId }) {
  const sx = useOnboardingSx();
  const navigate = useNavigate();
  const {
    handleBack,
    handleContinue,
    handleSkip,
    handleClearStep,
    handleStartOver,
    handleGoLive,
    currentStep,
    steps,
    organisationId,
    profileId,
    isSaving,
  } = useWizard();

  const [snack, setSnack] = useState({ open: false, message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });

  const saving = isSaving;
  const config = getStepById(stepId);
  const isOptional = config && !config.required;
  const isLastStep = stepId === LAST_STEP_ID;
  const stepTitle = getStepById(currentStep)?.title ?? 'this step';
  const clearStepBlockReason = getClearStepBlockReason({
    currentStepId: currentStep,
    steps,
    organisationId,
    profileId,
  });
  const canClearStep = clearStepBlockReason === null;

  const confirmOptions = useMemo(() => {
    if (confirmDialog.type === 'startOver') {
      return {
        title: 'Start over?',
        text: 'All onboarding progress will be cleared and you will return to step 1.',
        confirmButtonText: 'Start over',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'error',
        cancelButtonColor: 'inherit',
      };
    }

    if (confirmDialog.type === 'clear') {
      return {
        title: `Clear ${stepTitle}?`,
        text: 'Fields on this step will be reset. Later steps stay unchanged.',
        confirmButtonText: 'Clear step',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'warning',
        cancelButtonColor: 'inherit',
      };
    }

    if (confirmDialog.type === 'skip') {
      return {
        title: config?.skipConfirmTitle || `Skip ${stepTitle}?`,
        text:
          config?.skipConfirmText ||
          'You can return to this step later. Continue to the next step now.',
        confirmButtonText: config?.skipLabel || 'Skip',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'primary',
        cancelButtonColor: 'inherit',
      };
    }

    return null;
  }, [config?.skipConfirmText, config?.skipConfirmTitle, config?.skipLabel, confirmDialog.type, stepTitle]);

  const onContinue = () => {
    void handleContinue();
  };

  const onCompleteSetup = async () => {
    const result = await handleGoLive();
    if (result.ok) {
      setSnack({
        open: true,
        message: 'Congratulations! Your setup is now live.',
      });
      setTimeout(() => {
        navigate('/r/dashboard');
      }, 2000);
    } else if (result.message) {
      setSnack({ open: true, message: result.message });
    }
  };

  const handleConfirmClose = async (confirmed) => {
    const actionType = confirmDialog.type;
    setConfirmDialog({ open: false, type: null });

    if (!confirmed || !actionType) return;

    if (actionType === 'clear') {
      await handleClearStep();
      return;
    }

    if (actionType === 'startOver') {
      await handleStartOver();
      return;
    }

    if (actionType === 'skip') {
      await handleSkip();
    }
  };

  return (
    <>
      <Box sx={sx.wizardFooter}>
        <Box sx={sx.wizardFooterGroup}>
          <Button
            variant="outlined"
            disabled={currentStep <= 1 || saving}
            onClick={handleBack}
            sx={sx.backButton}
          >
            Back
          </Button>
        </Box>
        <Box sx={sx.wizardFooterGroup}>
          <Tooltip title={clearStepBlockReason || 'Reset fields on this step only'} arrow>
            <span>
              <Button
                variant="text"
                color="inherit"
                size="small"
                disabled={saving || !canClearStep}
                onClick={() => setConfirmDialog({ open: true, type: 'clear' })}
                sx={sx.textButton}
              >
                Clear this step
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="text"
            color="inherit"
            size="small"
            disabled={saving}
            onClick={() => setConfirmDialog({ open: true, type: 'startOver' })}
            sx={sx.textButton}
          >
            Start over
          </Button>
          {isOptional && (
            <Button
              variant="text"
              disabled={saving}
              onClick={() => setConfirmDialog({ open: true, type: 'skip' })}
              sx={sx.textButton}
            >
              {config?.skipLabel || 'Skip'}
            </Button>
          )}
          {isLastStep ? (
            <Button
              variant="contained"
              color="success"
              disabled={saving}
              onClick={() => void onCompleteSetup()}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={sx.completeButton}
            >
              {saving ? 'Completing…' : 'Complete setup'}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={saving}
              onClick={onContinue}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={sx.continueButton}
            >
              {saving ? 'Saving…' : 'Save & Continue'}
            </Button>
          )}
        </Box>
      </Box>

      {confirmOptions && (
        <ConfirmBox
          open={confirmDialog.open}
          onClose={handleConfirmClose}
          options={confirmOptions}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </>
  );
}
